import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MdOutlineArrowBackIos } from "react-icons/md";
import { FiTrash2 } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { fetchPetFinderById, updatePetFinder, deletePetFinder } from "../../redux/slices/petFinderSlice";
import { FiEdit3 } from "react-icons/fi";
import DeleteModal from "../../components/common/DeleteModal";
import { toast } from "react-toastify";

export default function PetFinderEdit() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { detail: petFinder, detailStatus } = useSelector((state) => state.petFinder);

  const [formData, setFormData] = useState({
    pet_name: "",
    breed: "",
    last_known_location: "",
    reward: "",
    additional_info: "",
    sex: "",
    age: "",
    physical_marks: "",
    color: "",
    active: true
  });
  const [images, setImages] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (id) dispatch(fetchPetFinderById(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (petFinder) {
      setFormData({
        pet_name: petFinder.pet_name || "",
        breed: petFinder.breed || "",
        last_known_location: petFinder.last_known_location || "",
        reward: petFinder.reward || "",
        additional_info: petFinder.additional_info || "",
        sex: petFinder.sex || "",
        age: petFinder.age || "",
        physical_marks: petFinder.physical_marks || "",
        color: petFinder.color || "",
        active: petFinder.active || false
      });
    }
  }, [petFinder]);

  if (detailStatus === "loading") return <div className="px-20 pt-10">Loading...</div>;
  if (detailStatus === "failed") return <div className="px-20 pt-10">Failed to load pet finder data.</div>;
  if (!petFinder) return <div className="px-20 pt-10">Pet finder not found.</div>;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    console.log('Selected files:', files);
    setImages(prev => [...prev, ...files]);
  };

  const handleSave = async () => {
    console.log('PetFinderEdit: Saving pet finder data:', {
      id,
      formData,
      imagesCount: images.length,
      petFinder: petFinder,
      existingImagesCount: petFinder?.image_urls?.length || 0
    });

    try {
      // Create FormData for backend (as expected by Postman example)
      const formDataToSend = new FormData();

      // Add pet finder fields in bracket notation as expected by backend
      if (formData.pet_name) formDataToSend.append('pet_finder[pet_name]', formData.pet_name);
      if (formData.breed) formDataToSend.append('pet_finder[breed]', formData.breed);
      if (formData.last_known_location) formDataToSend.append('pet_finder[last_known_location]', formData.last_known_location);
      if (formData.reward) formDataToSend.append('pet_finder[reward]', formData.reward);
      if (formData.additional_info) formDataToSend.append('pet_finder[additional_info]', formData.additional_info);
      if (formData.sex) formDataToSend.append('pet_finder[sex]', formData.sex);
      if (formData.age) formDataToSend.append('pet_finder[age]', formData.age);
      if (formData.physical_marks) formDataToSend.append('pet_finder[physical_marks]', formData.physical_marks);
      if (formData.color) formDataToSend.append('pet_finder[color]', formData.color);
      formDataToSend.append('pet_finder[active]', formData.active ? '1' : '0');

      // For updates, send existing image URLs along with new images
      if (petFinder.image_urls && petFinder.image_urls.length > 0) {
        petFinder.image_urls.forEach((imageUrl) => {
          formDataToSend.append('pet_finder[images][]', imageUrl);
        });
      }

      // Add new images
      images.forEach((image) => {
        formDataToSend.append('pet_finder[images][]', image);
      });

      console.log('PetFinderEdit: FormData to send to backend:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}: ${value}`);
      }

      // Update pet finder
      await dispatch(updatePetFinder({ id, data: formDataToSend })).unwrap();

      toast.success('Pet finder updated successfully!');
      navigate(-1);
    } catch (error) {
      console.error('PetFinderEdit: Save failed:', error);
      const errorMessage = error?.response?.data?.meta?.message || 'Failed to save pet finder. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleDelete = () => {
    dispatch(deletePetFinder(id));
    setModalOpen(false);
    navigate(-2); // Go back after delete
  };

  return (
    <div className="relative min-h-screen bg-white font-inter px-6 pt-12 pb-20">
      {/* Top bar */}
      <div className="flex justify-between items-start mb-10">
        <div className="flex items-start">
          <button
            onClick={() => navigate(-1)}
            aria-label="Go Back"
            className="mt-1 mr-3 p-2 text-2xl text-gray-800 hover:text-blue-600"
          >
            <MdOutlineArrowBackIos />
          </button>
          <div>
            <div className="text-xl font-bold text-black">
              Edit Pet Finder: {formData.pet_name}
            </div>
            <div className="text-gray-600 text-sm mt-1">Update pet finder information</div>
          </div>
        </div>

        <div className="flex gap-3">
          {/* Delete Button */}
          <button
            onClick={() => setModalOpen(true)}
            className="p-2 text-gray-600 hover:text-red-500"
          >
            <FiTrash2 size={20} />
          </button>

          <button
            onClick={() => navigate(-1)}
            className="border border-gray-300 rounded-lg px-4 py-2 text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-[#347DFF] text-white rounded-lg px-5 py-2 text-base font-medium hover:bg-blue-600 transition"
          >
            Save Changes
          </button>
        </div>
      </div>

      {/* Form Sections */}
      <div className="space-y-10 px-10">
        {/* Basic Pet Information */}
        <div>
          <div className="flex items-center gap-1 text-gray-700 font-medium mb-5">
            <span className="rounded p-1">
              <FiEdit3 size={22} />
            </span>{" "}
            Pet Information
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-1">Pet Name</label>
              <input
                name="pet_name"
                value={formData.pet_name}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter pet name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Breed</label>
              <input
                name="breed"
                value={formData.breed}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter breed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sex</label>
              <select
                name="sex"
                value={formData.sex}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Sex</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Age</label>
              <input
                name="age"
                value={formData.age}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter age"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Color</label>
              <input
                name="color"
                value={formData.color}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter color"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Last Known Location</label>
              <input
                name="last_known_location"
                value={formData.last_known_location}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter last known location"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Reward</label>
              <input
                name="reward"
                value={formData.reward}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter reward amount"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Physical Marks</label>
              <input
                name="physical_marks"
                value={formData.physical_marks}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter physical marks"
              />
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div>
          <div className="flex items-center gap-1 text-gray-700 font-medium mb-5">
            <span className="rounded p-1">📝</span> Additional Information
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              name="additional_info"
              value={formData.additional_info}
              onChange={handleChange}
              rows="6"
              className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
              placeholder="Enter additional information about the pet..."
            />
          </div>
        </div>

        {/* Images */}
        <div>
          <div className="flex items-center gap-1 text-gray-700 font-medium mb-5">
            <span className="rounded p-1">📷</span> Images
          </div>
          <div>
            {/* Existing Images */}
            {petFinder.image_urls && petFinder.image_urls.length > 0 && (
              <div className="mb-4">
                <div className="text-sm text-gray-600 mb-2">
                  Existing Images:
                </div>
                <div className="flex flex-wrap gap-4">
                  {petFinder.image_urls.map((image, index) => (
                    <img
                      key={`existing-${index}`}
                      src={image}
                      alt={`Existing image ${index + 1}`}
                      className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Upload New Images */}
            <div>
              <label className="block text-sm font-medium mb-1">Upload Additional Images</label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {images.length > 0 && (
                <div className="mt-4">
                  <div className="text-sm text-gray-600 mb-2">
                    {images.length} new image(s) selected - Preview:
                  </div>
                  <div className="flex flex-wrap gap-4">
                    {images.map((image, index) => (
                      <img
                        key={`new-${index}`}
                        src={URL.createObjectURL(image)}
                        alt={`New preview ${index + 1}`}
                        className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status */}
        <div>
          <div className="flex items-center gap-1 text-gray-700 font-medium mb-5">
            <span className="rounded p-1">⚙️</span> Status
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="active"
              checked={formData.active}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label className="text-sm font-medium text-gray-700">Active</label>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <DeleteModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onDelete={handleDelete}
        profileType="Pet Finder"
      />
    </div>
  );
}
